import { useEffect, useMemo, useState } from 'react';
import type { FieldErrors } from '../api/errors';
import type { Service } from '../api/types';
import { stripRequiredMarker } from '../domain/labels';
import { categorizeServices, categoryOfService, findCategory, findService, servicePriceLabel, type ServiceCategory } from '../domain/services';
import { systemFieldRequirement } from '../domain/validation';
import { selectHeading, selectParagraph, selectSelectedService, shouldShowError } from '../state/selectors';
import { useEnv, useFormState, useMoney } from './context';
import { Field } from './fields/Field';
import { SelectField, TextField } from './fields/inputs';
import { Html } from './Html';
import { CheckIcon } from './icons';
import { Section } from './Section';

interface Props {
  errors: FieldErrors;
}

export function ServicePicker({ errors }: Props) {
  const { controller, config } = useEnv();
  const money = useMoney();
  const state = useFormState();
  const services = state.bootstrap?.services ?? [];
  const selected = selectSelectedService(state);
  const selection = state.values.services[0];
  const form = state.bootstrap?.form ?? null;
  const serviceField = systemFieldRequirement(form, 'service', { requireZip: false });
  const show = (key: string) => (shouldShowError(state, key, 2) ? Array.from(new Set([...(errors[key] ?? []), ...(state.apiErrors[key] ?? [])])) : []);
  const categories = useMemo(() => categorizeServices(services), [services]);

  // Deep-linked service (?service=): the picker stays hidden while the linked service is the
  // selection — just the chosen package and a small link to change it.
  const preselectedId = useMemo(() => {
    const match = findService(services, config.preselectService);
    return typeof match?.id === 'number' ? match.id : null;
  }, [services, config.preselectService]);
  const [changing, setChanging] = useState(false);
  const lockedToPreselect = !changing && preselectedId !== null && selected?.id === preselectedId;

  // Category is view state: the selected service's, else the ?vehicle= preselect, else the only one.
  const [categoryId, setCategoryId] = useState<number | null>(() => {
    const fromService = categoryOfService(categories, selected?.id)?.id;
    if (fromService !== undefined) return fromService;
    const preselected = findCategory(categories, config.preselectVehicle);
    if (preselected) return preselected.id;
    return categories.length === 1 ? categories[0]!.id : null;
  });
  useEffect(() => {
    const own = categoryOfService(categories, selected?.id);
    if (own && own.id !== categoryId) setCategoryId(own.id);
  }, [categories, selected?.id, categoryId]);

  const category = categories.find((c) => c.id === categoryId) ?? null;
  const pickCategory = (next: ServiceCategory) => {
    setCategoryId(next.id);
    if (selected && !next.services.some((s) => s.id === selected.id)) controller.send({ type: 'SERVICE_SELECTED', serviceId: null });
  };
  const pickService = (id: number) => {
    controller.send({ type: 'SERVICE_SELECTED', serviceId: id });
    controller.send({ type: 'FIELD_BLURRED', key: 'services' });
  };

  const extrasHeading = selectHeading(state, 'extras_section');
  const extras = (selected?.extras ?? []).filter((e) => typeof e.id === 'number').sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0));
  const params = (selected?.pricing_parameters ?? []).filter((p) => typeof p.id === 'number').sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0));
  // The tenant's "service type" label applies only when they show that field; otherwise our own wording.
  const typeField = form?.system_fields?.service_type;
  const typeLabel = (typeField?.show !== false && stripRequiredMarker(typeField?.label).text) || 'What are we detailing?';

  return (
    <Section heading={selectHeading(state, 'service_section')} paragraph={selectParagraph(state, 'service_information')}>
      {lockedToPreselect && selected ? (
        <div className="cbf-field" data-cbf-field="services">
          <span className="cbf-label">{serviceField.label}</span>
          <div className="cbf-selected" aria-live="polite">
            <span className="cbf-selected-name">{selected.name}</span>
            {servicePriceLabel(selected, money) ? <span className="cbf-price" style={{ fontSize: 22 }}>{servicePriceLabel(selected, money)}</span> : null}
          </div>
          <button type="button" className="cbf-linklike" onClick={() => setChanging(true)}>
            Change service
          </button>
        </div>
      ) : categories.length === 0 ? (
        <>
          <SelectField
            fieldKey="services"
            label={serviceField.label}
            required
            errors={show('services')}
            value={selection ? String(selection.serviceId) : ''}
            placeholder="Choose a package…"
            options={services.filter((s) => typeof s.id === 'number').map((s) => ({ value: String(s.id), label: s.name ?? `Service ${s.id}` }))}
            onChange={(value) => controller.send({ type: 'SERVICE_SELECTED', serviceId: value ? Number(value) : null })}
            onBlur={() => controller.send({ type: 'FIELD_BLURRED', key: 'services' })}
          />
          {selected ? (
            <div className="cbf-selected" aria-live="polite">
              <span className="cbf-selected-name">{selected.name}</span>
              {servicePriceLabel(selected, money) ? <span className="cbf-price" style={{ fontSize: 22 }}>{servicePriceLabel(selected, money)}</span> : null}
            </div>
          ) : null}
        </>
      ) : (
        <Field fieldKey="services" label={typeLabel} required errors={show('services')} help={selectParagraph(state, 'service_type_information')} group>
          {() => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div className="cbf-pills" role="radiogroup" aria-label={typeLabel}>
                {categories.map((c) => {
                  const on = c.id === categoryId;
                  const id = `cbf-category-${c.id}`;
                  return (
                    <label key={c.id} className={`cbf-pill${on ? ' is-on' : ''}`} htmlFor={id}>
                      <input id={id} type="radio" name="cbf-category" checked={on} onChange={() => pickCategory(c)} />
                      <span>{c.name}</span>
                      <span className="cbf-pill-count" aria-hidden="true">
                        {c.services.length}
                      </span>
                      <span className="cbf-sr">, {c.services.length === 1 ? '1 package' : `${c.services.length} packages`}</span>
                    </label>
                  );
                })}
              </div>

              {category ? (
                <div className="cbf-packages" role="radiogroup" aria-label={`${serviceField.label} — ${category.name}`}>
                  {category.services.map((service: Service) => {
                    const on = service.id === selected?.id;
                    const id = `cbf-service-${service.id}`;
                    const price = servicePriceLabel(service, money);
                    return (
                      <label key={service.id} className={`cbf-choice${on ? ' is-on' : ''}`} htmlFor={id}>
                        <input id={id} type="radio" name="cbf-service" checked={on} onChange={() => pickService(service.id!)} />
                        <span className="cbf-radio" aria-hidden="true" />
                        <span className="cbf-choice-body">
                          <span className="cbf-choice-title">
                            <span>{service.name}</span>
                            {price ? <span className="cbf-price">{price}</span> : null}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="cbf-help">Choose a type above to see its packages.</p>
              )}
            </div>
          )}
        </Field>
      )}

      {selected && params.length ? (
        <div className="cbf-row cbf-row-2">
          {params.map((param) => (
            <TextField
              key={param.id}
              fieldKey={`services.pricing.${param.id}`}
              type="number"
              inputMode="numeric"
              label={param.name?.trim() || 'Quantity'}
              required
              min={param.quantity_minimum ?? 0}
              max={param.quantity_maximum ?? undefined}
              step={1}
              help={typeof param.price === 'number' ? `${money(param.price)} each` : null}
              value={selection?.pricingParameters[param.id!] !== undefined ? String(selection.pricingParameters[param.id!]) : ''}
              onChange={(value) => controller.send({ type: 'PRICING_PARAM_SET', paramId: param.id!, quantity: Math.max(0, Math.trunc(Number(value) || 0)) })}
              onBlur={() => controller.send({ type: 'FIELD_BLURRED', key: 'services' })}
            />
          ))}
        </div>
      ) : null}

      {selected && extras.length ? (
        <Field fieldKey="services.extras" label={extrasHeading ?? 'Extras'} help={selectParagraph(state, 'extra_information')} group>
          {() => (
            <div className="cbf-choice-grid">
              {extras.map((extra) => {
                const qty = selection?.extras[extra.id!] ?? 0;
                const on = qty > 0;
                const id = `cbf-extra-${extra.id}`;
                return (
                  <label key={extra.id} className={`cbf-choice${on ? ' is-on' : ''}`} style={{ alignItems: 'flex-start' }} htmlFor={id}>
                    <input id={id} type="checkbox" checked={on} onChange={(e) => controller.send({ type: 'EXTRA_SET', extraId: extra.id!, quantity: e.target.checked ? 1 : 0 })} />
                    <span className="cbf-check" aria-hidden="true">
                      {on ? <CheckIcon size={16} /> : null}
                    </span>
                    <span className="cbf-choice-body">
                      <span className="cbf-choice-title">
                        <span>{extra.name}</span>
                        {typeof extra.price === 'number' ? <span className="cbf-price">+ {money(extra.price)}</span> : null}
                      </span>
                      {extra.description ? <Html as="span" className="cbf-help" html={extra.description} /> : null}
                      {on && extra.quantity_based ? (
                        <span className="cbf-qty" onClick={(e) => e.preventDefault()}>
                          <label className="cbf-help" htmlFor={`${id}-qty`}>
                            Quantity
                          </label>
                          <input id={`${id}-qty`} className="cbf-input" type="number" min={1} max={10000} value={qty} onChange={(e) => controller.send({ type: 'EXTRA_SET', extraId: extra.id!, quantity: Math.max(1, Math.trunc(Number(e.target.value) || 1)) })} />
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </Field>
      ) : null}
    </Section>
  );
}
